import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/js/firebase';
import { logger } from '@/services/loggerService';

export const useClientDeliverablesStore = defineStore('clientDeliverables', () => {
  // State
  const items = ref([]);
  const loading = ref(false);
  const initialized = ref(false);
  const currentCompanyId = ref(null);
  let unsubscribe = null;

  // Getters
  const deliverablesByProject = computed(() => {
    return (projectId) => {
      if (!projectId) return [];
      return items.value.filter(d => d.projectId === projectId);
    };
  });

  // Actions
  async function init(companyId) {
    if (!companyId) {
      logger.error('[ClientDeliverables] No company ID provided');
      return;
    }

    // If already initialized for this company, skip
    if (initialized.value && currentCompanyId.value === companyId) {
      logger.info('[ClientDeliverables] Already initialized for this company', {
        companyId,
        deliverablesCount: items.value.length
      });
      return;
    }

    // If switching companies, clean up previous subscription
    if (currentCompanyId.value && currentCompanyId.value !== companyId) {
      logger.info('[ClientDeliverables] Switching companies, resetting store', {
        from: currentCompanyId.value,
        to: companyId
      });
      reset();
    }

    try {
      loading.value = true;
      currentCompanyId.value = companyId;

      logger.info('[ClientDeliverables] Initializing deliverables', { companyId });

      // Set up real-time listener for deliverables with companyId
      const deliverablesRef = collection(db, 'deliverables');
      const q = query(deliverablesRef, where('companyId', '==', companyId));

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const deliverables = [];

          snapshot.forEach((doc) => {
            deliverables.push({
              id: doc.id,
              ...doc.data()
            });
          });

          // Sort by due date
          deliverables.sort((a, b) => {
            const dateA = a.dueDate?.toMillis ? a.dueDate.toMillis() : 0;
            const dateB = b.dueDate?.toMillis ? b.dueDate.toMillis() : 0;
            return dateA - dateB;
          });

          items.value = deliverables;
          initialized.value = true;
          loading.value = false;

          logger.info('[ClientDeliverables] Deliverables loaded', {
            companyId,
            count: deliverables.length
          });
        },
        (error) => {
          logger.firestoreListenerError('[ClientDeliverables] Error loading deliverables', error, { companyId });

          // On permission error, just set empty array
          if (error.code === 'permission-denied') {
            items.value = [];
            initialized.value = true;
          }

          loading.value = false;
        }
      );
    } catch (error) {
      logger.error('[ClientDeliverables] Failed to initialize', {
        companyId,
        error: error.message
      });
      loading.value = false;
      throw error;
    }
  }

  function reset() {
    logger.info('[ClientDeliverables] Resetting store');

    // Unsubscribe from listener
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }

    items.value = [];
    loading.value = false;
    initialized.value = false;
    currentCompanyId.value = null;
  }

  function getDeliverablesByProject(projectId) {
    return deliverablesByProject.value(projectId);
  }

  return {
    // State
    items,
    loading,
    initialized,
    currentCompanyId,

    // Getters
    deliverablesByProject,

    // Actions
    init,
    reset,
    getDeliverablesByProject
  };
});
